import { FormEvent, useMemo, useState } from "react";
import { Button, Col, Container, Form, Input, Message, Row } from "rsuite";
import styles from "./style.module.css";
import { useDispatch } from "react-redux";
import { bindActionCreators } from "redux";
import { actionCreators } from "../state";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { APIResult, User } from "../my-type";
import { getCurrentUserProfile, registerAccount } from "../service/AuthService";
import getTeamCodeByHost from "../util/teamCode";

export default function RegisterAccount() {
  const dispatch = useDispatch();
  const { updateUser } = bindActionCreators(actionCreators, dispatch);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectLocation = searchParams.get("location");
  const teamCode = useMemo(() => getTeamCodeByHost(), []);

  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    if (!teamCode) {
      setErrorMessage("無法識別團隊代碼，請稍後再試或聯絡客服。");
      return false;
    }

    if (!displayName || !username || !password) {
      setErrorMessage("請填寫必填欄位。");
      return false;
    }

    if (password.length < 8) {
      setErrorMessage("密碼至少需 8 碼。");
      return false;
    }

    if (password !== confirmPassword) {
      setErrorMessage("兩次輸入的密碼不一致。");
      return false;
    }

    return true;
  };

  const handleRegister = async (_formValue: Record<string, any> | null, event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const result = await registerAccount({
        teamCode,
        username,
        password,
        displayName,
        email: email || undefined,
      });

      if (!result || result.statusCode !== 1) {
        setErrorMessage(result?.message || "註冊失敗，請稍後重試。");
        return;
      }

      const token = result.data?.token;
      if (!token) {
        setSuccessMessage("註冊成功，請返回登入頁登入。");
        return;
      }

      const profileResult: APIResult | null = await getCurrentUserProfile(token);
      if (!profileResult || profileResult.statusCode !== 1) {
        setSuccessMessage("註冊成功，請使用帳號密碼登入。");
        return;
      }

      const fetchedUser: User = profileResult.data;
      const normalizedUser: User = {
        ...fetchedUser,
        userId: fetchedUser.userId ?? username,
        jwtToken: token,
        refreshToken: fetchedUser.refreshToken ?? "",
        lineAccessToken: fetchedUser.lineAccessToken ?? null,
        tokenExpiry: fetchedUser.tokenExpiry ? new Date(fetchedUser.tokenExpiry) : null,
      };

      updateUser(normalizedUser);
      setSuccessMessage("註冊並登入成功，正在為您導向...");
      setTimeout(() => {
        if (redirectLocation) {
          navigate(redirectLocation);
        } else {
          navigate("/");
        }
      }, 800);
    } catch (error) {
      console.error("register failed", error);
      setErrorMessage("註冊過程發生錯誤，請稍後再試。");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPageContainer}>
      <Container>
        <Row className={styles.centerRow}>
          <Col xs={24} sm={18} md={12} lg={10} xl={8} className={styles.loginFrame}>
            <div className={styles.loginContent}>
              <h4 className={styles.loginTitle}>建立新帳號</h4>
              <p className={styles.subText}>請輸入基本資料以建立傳統帳號</p>
              <Form fluid className={styles.traditionalLoginForm} onSubmit={handleRegister}>
                <Form.Group className={styles.formGroup}>
                  <Form.ControlLabel>顯示名稱 *</Form.ControlLabel>
                  <Input value={displayName} onChange={setDisplayName} placeholder="例如：王小明" />
                </Form.Group>
                <Form.Group className={styles.formGroup}>
                  <Form.ControlLabel>電子郵件</Form.ControlLabel>
                  <Input type="email" value={email} onChange={setEmail} placeholder="example@mail.com" />
                </Form.Group>
                <Form.Group className={styles.formGroup}>
                  <Form.ControlLabel>登入帳號 *</Form.ControlLabel>
                  <Input value={username} onChange={setUsername} placeholder="帳號" autoComplete="username" />
                </Form.Group>
                <Form.Group className={styles.formGroup}>
                  <Form.ControlLabel>密碼 *</Form.ControlLabel>
                  <Input type="password" value={password} onChange={setPassword} placeholder="至少 8 碼" autoComplete="new-password" />
                </Form.Group>
                <Form.Group className={styles.formGroup}>
                  <Form.ControlLabel>確認密碼 *</Form.ControlLabel>
                  <Input type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="再輸入一次密碼" autoComplete="new-password" />
                </Form.Group>

                {errorMessage && (
                  <Message showIcon type="error" className={styles.errorMessage}>
                    {errorMessage}
                  </Message>
                )}
                {successMessage && (
                  <Message showIcon type="success" className={styles.errorMessage}>
                    {successMessage}
                  </Message>
                )}

                <Button
                  appearance="primary"
                  className={styles.accountLoginBtn}
                  loading={loading}
                  disabled={loading}
                  type="submit"
                >
                  建立帳號
                </Button>
              </Form>
              <Button appearance="link" onClick={() => navigate("/login")}>
                返回登入
              </Button>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
